import React, {Component} from "react";
import {Navigate} from "react-router-dom";
import {APIClient} from "../../../utils/APIClient";
import {API_URL} from "../../../settings/local_settings";
import {AccessService} from "../../../utils/AccessService";
import {useFicha21} from "../../../context/UserModulesContext";
import {ClientHeader} from "../ClientHeader";
import {ClientTabs} from "../ClientTabs";
import {Spinner} from "../../Spinner";
import {FaIcon} from "../../FaIcon";
import {ProjectInstructionsBlock} from "./ProjectInstructionsBlock";
import {PromptLibrarySection} from "./PromptLibrarySection";
import {copyStatusAfterCopy, fetchPromptCopyStatus} from "../../prompts/promptCopyPing";
import {withTranslation} from 'react-i18next';

const ERROR_NO_MODULE = 'NO_MODULE';
const ERROR_NOT_FOUND = 'NOT_FOUND';
const ERROR_GENERIC = 'GENERIC';

class ClientIAAssistantContentClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clientName: '',
            // null = cargando; cuando termina queda {} o se setea errorKind
            starterPack: null,
            // Estado "qué versión copió la agencia PARA este cliente" (chip de versión).
            copyStatus: {},
            errorKind: null,
            loaded: false,
            activeSubTab: 'connect', // 'connect' = Starter Pack | 'library' = Biblioteca de prompts
        };
        this.handlePromptCopied = this.handlePromptCopied.bind(this);
    }

    componentDidMount() {
        this.getClientData();
        this.maybeGetStarterPack();
    }

    // Los módulos llegan del provider y pueden no estar en el primer render: sin esperarlos, el
    // "tu plan no incluye el módulo MCP" se mostraría a todos por un instante.
    componentDidUpdate(prevProps) {
        if (!prevProps.modulesLoaded && this.props.modulesLoaded) this.maybeGetStarterPack();
    }

    maybeGetStarterPack() {
        if (!this.props.modulesLoaded || this._starterPackRequested) return;
        this._starterPackRequested = true;
        this.getStarterPack();
    }

    getClientData() {
        new APIClient(API_URL + 'client/' + this.props.clientId).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                this.setState({clientName: data.data['name']});
            }
        });
    }

    getStarterPack() {
        // El gating del tab (ClientTabs) ya asegura módulo MCP + SuperAdmin, pero chequeamos el
        // módulo igual antes de pegarle al endpoint para mostrar el mensaje claro de "tu plan no
        // incluye el módulo MCP" sin depender de poder distinguir el 403 (APIClient no expone el
        // HTTP status code). Los módulos vienen del provider, no de un fetch propio.
        const modules = this.props.modules;
        if (!modules) {
            this.setState({errorKind: ERROR_GENERIC, loaded: true});
            return;
        }
        if (!modules.includes(AccessService.MODULE_DASHBO_MCP)) {
            this.setState({errorKind: ERROR_NO_MODULE, loaded: true});
            return;
        }
        this.fetchStarterPack();
    }

    fetchStarterPack() {
        const url = API_URL + 'agent/client_starter_pack/?agency_client_id=' + this.props.clientId;
        new APIClient(url).call('GET').then((data) => {
            if (data && data.status !== 'ERROR' && data.status !== 'NETWORK_ERROR'
                && data.instructions_markdown !== undefined) {
                this.setState({starterPack: data, errorKind: null, loaded: true});
            } else {
                this.setState({errorKind: this.resolveErrorKind(data), loaded: true});
            }
        });
        fetchPromptCopyStatus({agencyClientId: this.props.clientId})
            .then((copyStatus) => this.setState({copyStatus}));
    }

    // Actualización optimista tras copiar (helper único — las reglas viven con él).
    handlePromptCopied(promptId, version) {
        this.setState((state) => ({
            copyStatus: copyStatusAfterCopy(state.copyStatus, promptId, version),
        }));
    }

    resolveErrorKind(data) {
        // APIClient no expone el HTTP status; mapeamos por el texto del backend con
        // fallback genérico. El 403 (sin módulo) ya se atajó antes con el chequeo de
        // módulo, así que acá basicamente distinguimos 404 (cliente) del resto.
        const msg = (data && (data.error_message || data.error)) || '';
        if (/no encontrado|sin acceso|not found/i.test(msg)) {
            return ERROR_NOT_FOUND;
        }
        if (/módulo mcp|modulo mcp|mcp module/i.test(msg)) {
            return ERROR_NO_MODULE;
        }
        return ERROR_GENERIC;
    }

    errorMessageKey() {
        switch (this.state.errorKind) {
            case ERROR_NO_MODULE:
                return 'ia_assistant.error_no_module';
            case ERROR_NOT_FOUND:
                return 'ia_assistant.error_not_found';
            default:
                return 'ia_assistant.error_generic';
        }
    }

    renderBody() {
        const {t} = this.props;

        if (!this.state.loaded) {
            return (
                <div className="box-loader">
                    <Spinner color="positive"/>
                </div>
            );
        }

        if (this.state.errorKind) {
            return (
                <div className="ia-assistant-error">
                    <FaIcon iconName="circle-exclamation"/>
                    <p>{t(this.errorMessageKey())}</p>
                </div>
            );
        }

        const pack = this.state.starterPack || {};
        const {activeSubTab} = this.state;
        return (
            <div className="ia-assistant-page">
                <header className="ia-assistant-hero">
                    <div className="ia-assistant-hero-icon">
                        <FaIcon iconName="robot"/>
                    </div>
                    <div className="ia-assistant-hero-text">
                        <h2>{t('ia_assistant.hero_title')}</h2>
                        <p>{t('ia_assistant.hero_subtitle')}</p>
                    </div>
                </header>

                <nav className="ia-assistant-subtabs" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeSubTab === 'connect'}
                        className={`ia-assistant-subtab ${activeSubTab === 'connect' ? 'is-active' : ''}`}
                        onClick={() => this.setState({activeSubTab: 'connect'})}
                    >
                        {t('ia_assistant.subtab_connect')}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeSubTab === 'library'}
                        className={`ia-assistant-subtab ${activeSubTab === 'library' ? 'is-active' : ''}`}
                        onClick={() => this.setState({activeSubTab: 'library'})}
                    >
                        {t('ia_assistant.subtab_library')}
                    </button>
                </nav>

                {activeSubTab === 'connect' ? (
                    <div className="ia-assistant-subpanel">
                        <div className="ia-assistant-steps">
                            <h3 className="ia-assistant-steps-title">{t('ia_assistant.steps_title')}</h3>
                            <ol className="ia-assistant-steps-list">
                                <li>
                                    <span className="ia-assistant-step-title">{t('ia_assistant.step1_title')}</span>
                                    <span className="ia-assistant-step-desc">{t('ia_assistant.step1_desc')}</span>
                                </li>
                                <li>
                                    <span className="ia-assistant-step-title">{t('ia_assistant.step2_title')}</span>
                                    <span className="ia-assistant-step-desc">{t('ia_assistant.step2_desc')}</span>
                                </li>
                                <li>
                                    <span className="ia-assistant-step-title">{t('ia_assistant.step3_title')}</span>
                                    <span className="ia-assistant-step-desc">{t('ia_assistant.step3_desc')}</span>
                                </li>
                            </ol>
                        </div>

                        <div className="ia-assistant-blocks">
                            <ProjectInstructionsBlock
                                minimalMarkdown={pack.minimal_markdown}
                                fullMarkdown={pack.instructions_markdown}
                                minimalPrompt={pack.minimal_prompt}
                                instructionsPrompt={pack.instructions_prompt}
                                copyStatus={this.state.copyStatus}
                                onCopied={this.handlePromptCopied}
                                clientId={this.props.clientId}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="ia-assistant-subpanel">
                        <PromptLibrarySection clientId={this.props.clientId}/>
                    </div>
                )}
            </div>
        );
    }

    render() {
        // Ficha 2.1: "Conectar con tu IA" vive dentro del tab IA — la ruta legacy redirige. En
        // clásico NO redirige: el switch devuelve esta pantalla standalone. Se espera a que los
        // módulos carguen para no mostrar un flash de la pantalla legacy antes del redirect.
        if (!this.props.modulesLoaded) return null;
        if (this.props.ficha21) {
            return <Navigate to={`/client/${this.props.clientId}/ia/connect`} replace/>;
        }
        return (
            <section className="section-content">
                <ClientHeader name={this.state.clientName}/>
                <ClientTabs clientId={this.props.clientId}/>

                <div className="section-body">
                    <div className="box">
                        {this.renderBody()}
                    </div>
                </div>
            </section>
        );
    }
}

const ClientIAAssistantContentInner = withTranslation()(ClientIAAssistantContentClass);

// Módulos y flag del rediseño salen del provider. Antes esta pantalla pedía `access/modules` DOS
// veces (una por el flag y otra por los módulos).
export function ClientIAAssistantContent(props) {
    const {ficha21, modules, loaded} = useFicha21();
    return <ClientIAAssistantContentInner {...props} ficha21={ficha21} modules={modules} modulesLoaded={loaded}/>;
}
